import React, { useCallback, useContext, useEffect, useState } from 'react';

import { SortField } from '@totem/components/ui_components/reportSettings/types';
import {
  Paging,
  PersistentData,
  PersistentDataItems,
  PersistentFilterFields,
} from '@totem/components/userContext/persistence/types';
import UserPersistenceContext from '@totem/components/userContext/persistence/UserPersistenceContext';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';
import { USER_ENDPOINT } from '@totem/utilities/endpoints';

type Props = {
  children?: React.ReactNode;
};

const UserPersistenceContainer = ({ children }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const [persistence, setPersistence] = useState<PersistentDataItems>([]);
  const [persistenceLoaded, setPersistenceLoaded] = useState(false);
  const [persistenceOrganizationId, setPersistenceOrganizationId] =
    useState('');
  const [isSending, setIsSending] = useState(false);

  // Initial load of persistence data, and reload whenever the organization
  // changes. persistenceLoaded is reset to false for the duration of a reload,
  // and persistenceOrganizationId is only updated once the reload settles —
  // consumers (e.g. InputContainer) must check persistenceOrganizationId
  // against the user's current organization before trusting persistenceLoaded.
  // A plain persistenceLoaded flag isn't enough on its own: a child
  // component's effect can run before this effect gets a chance to flip it
  // back to false, so it can still read a stale "true" pointing at the
  // previous organization's cached records for one render.
  useEffect(() => {
    if (
      isNotNull(userProfile) &&
      isNotNull(userProfile.id) &&
      userProfile.id !== '' &&
      isNotNull(userProfile.organization) &&
      userProfile.organization.id !== ''
    ) {
      const organizationId = userProfile.organization.id;

      setPersistenceLoaded(false);

      fetch(`${USER_ENDPOINT}/persistence`, {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      })
        .then((res) => res.json())
        .then((result: PersistentDataItems) => {
          if (isNotNull(result)) {
            setPersistence(result);
          } else {
            setPersistence([]);
          }
        })
        .finally(() => {
          setPersistenceOrganizationId(organizationId);

          setPersistenceLoaded(true);
        });
    } else {
      setPersistenceOrganizationId(userProfile?.organization?.id ?? '');

      setPersistenceLoaded(true);
    }
  }, [userProfile.id, userProfile.organization.id]);

  const sendUpdatePersistence = useCallback(async (record: PersistentData) => {
    if (!isSending) {
      setIsSending(true);

      fetch(`${USER_ENDPOINT}/persistence`, {
        method: 'PUT',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(record),
      }).finally(() => {
        setIsSending(false);
      });
    }
  }, []);

  const sendDeletePersistence = useCallback(async (componentKey: string) => {
    fetch(`${USER_ENDPOINT}/persistence/${componentKey}`, {
      method: 'DELETE',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    });
  }, []);

  const getComponentPersistence = (componentKey: string) => {
    const foundPersistenceData = persistence.find(
      (item) => item.componentKey === componentKey,
    );

    if (isNotNull(foundPersistenceData)) {
      return foundPersistenceData;
    }
    return null;
  };

  const updateComponentPersistence = (
    componentKey: string,
    label: string,
    paging: Paging,
    sort: SortField,
    filters: PersistentFilterFields,
  ) => {
    const existing = persistence.find(
      (item) => item.componentKey === componentKey,
    );

    const cleanFilters = filters.filter(
      (item) =>
        (isNotNull(item.value) && item.value !== '') ||
        (isNotNull(item.values) && item.values.length > 0),
    );

    const updatedRecord: PersistentData = isNotNull(existing)
      ? { ...existing, sort, filters: cleanFilters, paging, label }
      : {
          userId: userProfile.id,
          organizationId: userProfile.organization.id,
          componentKey,
          label,
          paging,
          sort,
          filters: cleanFilters,
        };

    setPersistence((prev) =>
      isNotNull(existing)
        ? prev.map((item) =>
            item.componentKey === componentKey ? updatedRecord : item,
          )
        : [...prev, updatedRecord],
    );

    sendUpdatePersistence(updatedRecord);
  };

  const deleteComponentPersistence = (componentKey: string) => {
    setPersistence((prev) =>
      prev.filter((item) => item.componentKey !== componentKey),
    );

    sendDeletePersistence(componentKey);
  };

  return (
    <UserPersistenceContext.Provider
      value={{
        persistenceData: persistence,
        persistenceLoaded,
        persistenceOrganizationId,
        getComponentPersistence,
        updateComponentPersistence,
        deleteComponentPersistence,
      }}
    >
      {children}
    </UserPersistenceContext.Provider>
  );
};

export default UserPersistenceContainer;
